"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

const SITE_KEY = "6Le9GgosAAAAACYw-DRpbkpA3aKJRq-L8mghAf7K";

const EXECUTE_TIMEOUT_MS = 5000;

/**
 * Why a submit went out with no reCAPTCHA token.
 *
 * These are the only two ways that happens, they both resolve a `null` token
 * and both always have. The reason is the whole of what this module gained:
 * it names *which* one, because they have different owners and the submit
 * looks identical from the outside either way.
 *
 * There is deliberately no reason for a `grecaptcha` that throws. A throw
 * rejects, the caller never reaches a submit, and there is no token state to
 * name — see `getCAPTCHAToken`.
 */
export const CAPTCHA_UNAVAILABLE_REASON = {
    /**
     * `window.grecaptcha` was not there to ask. Ours: the script below is not
     * rendered until the first `pointermove`, so a customer who taps straight
     * through — or one whose device never produces that event — reaches the pay
     * button before the script has been requested at all.
     */
    SCRIPT_ABSENT: "script_absent",
    /**
     * `execute` did not answer within our own deadline. Google's end, and the
     * bucket a refusal lands in too: a rejected `execute` is left unhandled on
     * purpose, so the deadline is what settles it.
     */
    EXECUTE_TIMEOUT: "execute_timeout",
    /**
     * `execute` rejected. Google's end, and the bucket a refusal lands in too: a rejected `execute` is left unhandled on
     * purpose, so the deadline is what settles it.
     */
    EXECUTE_REJECTED: "execute_rejected",
} as const;

export type CaptchaUnavailableReason =
    (typeof CAPTCHA_UNAVAILABLE_REASON)[keyof typeof CAPTCHA_UNAVAILABLE_REASON];

export type CaptchaTokenResult = {
    token: unknown;
    /** Present only when `token` is null. */
    reason?: CaptchaUnavailableReason;
};

export function getCAPTCHAToken(): Promise<CaptchaTokenResult> {
    return new Promise((resolve) => {
        if (!window.grecaptcha) {
            resolve({
                token: null,
                reason: CAPTCHA_UNAVAILABLE_REASON.SCRIPT_ABSENT,
            });
            return;
        }

        const timeout = setTimeout(() => {
            resolve({
                token: null,
                reason: CAPTCHA_UNAVAILABLE_REASON.EXECUTE_TIMEOUT,
            });
        }, EXECUTE_TIMEOUT_MS);

        // Nothing below is caught, and that is the behaviour rather than an
        // omission. Two shapes depend on it:
        //
        // 1. A *synchronous* throw rejects this promise. That is
        //    `window.grecaptcha` being a stub with no working `ready` — what
        //    an ad-blocker leaves behind. The rejection reaches `useSignUp`'s
        //    catch, which shows the payment error and never posts. Resolving
        //    a `null` token instead would read as an answer and charge the
        //    card, so every request that defeats the captcha would go
        //    through. Blocking those customers is the known cost and the
        //    deliberate one; revisit only once the backend is confirmed to
        //    reject a null token on its own.
        //
        // 2. A *rejected* `execute` is left unhandled, so the deadline above
        //    is what settles the promise — five seconds later, with
        //    `EXECUTE_TIMEOUT`. Attaching a `.catch` would settle it at once
        //    and name the refusal properly, which is the better shape and
        //    still a change to when the submit fires. It is not one telemetry
        //    is allowed to make.
        //
        // The deadline is likewise left armed on the throw path: it fires
        // into a promise that has already rejected, where `resolve` is a
        // no-op. Harmless, and load-bearing only in that clearing it would be
        // this module deciding something again.
        window.grecaptcha?.ready(function() {
            window.grecaptcha
                ?.execute(SITE_KEY, { action: "submit" })
                .then(function(token) {
                    clearTimeout(timeout);
                    resolve({ token });
                })
                .catch(function() {
                    clearTimeout(timeout);
                    resolve({
                        token: null,
                        reason: CAPTCHA_UNAVAILABLE_REASON.EXECUTE_REJECTED,
                    });
                });
        });
    });
}

const ReCAPTCHAScript = () => {
    const [render, setRender] = useState(false);

    useEffect(() => {
        const myListener = () => {
            document.removeEventListener("pointermove", myListener, false);
            setRender(true);
        };
        document.addEventListener("pointermove", myListener, false);
    }, []);

    if (!render) {
        return null;
    }

    return <Script src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`} />
};

export default ReCAPTCHAScript;
